// Export comptable global : classeur Excel (un onglet par section) pour la période.
import * as XLSX from 'xlsx';
import {
  fetchReceptions,
  fetchFinances,
  fetchVentes,
  fetchOperationsCoursier,
  fetchRegistreCalamites,
  decompte,
  nomTiers,
  labelQualite,
} from './dataProvider';
import { dansPeriode } from '../components/shared/PeriodeFiltre';

const ligne = (cells) => cells;

export async function exportComptable({ du, au }) {
  const [receptions, fin, ventes, ops, calamites] = await Promise.all([
    fetchReceptions(),
    fetchFinances(),
    fetchVentes(),
    fetchOperationsCoursier(),
    fetchRegistreCalamites({ du, au }),
  ]);

  const dans = (d) => dansPeriode(d, du, au);
  const nomCompte = (id) => fin.comptes.find((c) => c.id === id)?.nom ?? id ?? '';
  const wb = XLSX.utils.book_new();

  const feuille = (onglet, entetes, lignes, totalIdx) => {
    const rows = [entetes, ...lignes];
    if (totalIdx !== undefined && lignes.length) {
      const total = lignes.reduce((s, l) => s + Number(l[totalIdx] || 0), 0);
      const t = Array(entetes.length).fill('');
      t[0] = 'TOTAL';
      t[totalIdx] = Number(total.toFixed(3));
      rows.push([], t);
    }
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = entetes.map((h) => ({ wch: Math.max(12, Math.min(34, String(h).length + 4)) }));
    ws['!freeze'] = { xSplit: 0, ySplit: 1 };
    XLSX.utils.book_append_sheet(wb, ws, onglet.slice(0, 31));
  };

  const recs = receptions.filter((r) => dans(r.date));
  const vts = ventes.filter((v) => dans(v.date));
  const mvs = fin.mouvements.filter((m) => dans(m.date));
  const rgs = fin.reglements.filter((r) => dans(r.date));
  const opsP = ops.filter((o) => dans(o.date));

  feuille(
    'Synthèse',
    ['Rubrique', 'Valeur'],
    [
      ['Huilerie', 'Huilerie Hassan Ellouze'],
      ['Période — du', du || 'origine'],
      ['Période — au', au || "aujourd'hui"],
      ['Édité le', new Date().toISOString().slice(0, 16).replace('T', ' ')],
      ['Réceptions', recs.length],
      ['Ventes au comptoir', vts.length],
      ['Mouvements de caisse', mvs.length],
      ['Règlements & acomptes', rgs.length],
      ['Dépenses & opérations coursier', opsP.length],
      ['Retenues Fonds des Calamités', calamites.lignes.length],
      ['Total retenue Calamités (TND)', calamites.totaux.retenue],
      ['Dette Fonds de calamité (TND)', calamites.totaux.provision],
      ['Charge calamité huilerie (TND)', calamites.totaux.charge],
      ['Montant réceptions (TND)', Number(recs.reduce((s, r) => s + decompte(r).montant, 0).toFixed(3))],
      ['Chiffre d’affaires ventes (TND)', Number(vts.reduce((s, v) => s + Number(v.total || 0), 0).toFixed(3))],
    ]
  );

  feuille(
    'Réceptions',
    ['Réf', 'Date', 'Parcours', 'Tiers', 'Qualité', 'Poids net (kg)', 'Rendement (%)', 'Montant (TND)', 'Statut'],
    recs.map((r) => {
      const c = decompte(r);
      return [r.id, r.date, r.parcours, nomTiers(r.tiersId), r.qualite ? labelQualite(r.qualite) : '', c.poidsNet, c.rendement, Number(c.montant.toFixed(3)), r.statut];
    }),
    7
  );

  feuille(
    'Ventes comptoir',
    ['Réf', 'Date', 'Client', 'Format', 'Unités', 'Poids (kg)', 'Prix (TND/kg)', 'Total (TND)', 'Règlement'],
    vts.map((v) => [v.id, v.date, v.client, v.format, v.unites ?? '', v.kg, v.prixKg, v.total, v.paiement]),
    7
  );

  feuille(
    'Journal caisse',
    ['Date', 'Type', 'Compte', 'Compte cible', 'Catégorie', 'Libellé', 'N° pièce', 'Montant (TND)'],
    mvs.map((m) => [m.date, m.type, nomCompte(m.compteId), m.compteCibleId ? nomCompte(m.compteCibleId) : '', m.categorie ?? '', m.libelle ?? '', m.piece ?? '', m.montant]),
    7
  );

  feuille(
    'Règlements tiers',
    ['Date', 'Catégorie', 'Bénéficiaire', 'N° pièce', 'Mode', 'Compte', 'Nature', 'Montant (TND)', 'Statut'],
    rgs.map((r) => [r.date, r.categorie, r.beneficiaire ?? nomTiers(r.tiersId), r.piece ?? '', r.mode ?? '', nomCompte(r.compteId), r.acompte ? 'Acompte' : 'Règlement', r.montant, r.statut ?? '']),
    7
  );

  feuille(
    'Dépenses coursier',
    ['Réf', 'Date', 'Type', 'Catégorie / bénéficiaire', 'Motif', 'Montant (TND)', 'Statut'],
    opsP.map((o) => [o.id, o.date, o.typeOperation, o.categorie ?? nomTiers(o.tiersId ?? o.employeId), o.libelle ?? '', o.montant, o.statut]),
    5
  );

  feuille(
    'Fonds Calamités',
    ['N° document', 'Réf', 'Date', 'Fournisseur', 'Base brute (TND)', 'Retenue 1 % (TND)', 'Dette fonds (TND)', 'Charge huilerie (TND)'],
    calamites.lignes.map((l) => [l.numero || l.ref, l.ref, l.date, l.tiers, l.montantBrut, l.retenue, l.provision, l.charge]),
    5
  );

  feuille(
    'Soldes comptes',
    ['Compte', 'Type', 'Solde (TND)'],
    fin.comptes.map((c) => [c.nom, c.type, c.solde])
  );

  XLSX.writeFile(wb, `export-comptable_${du || 'origine'}_${au || 'ce-jour'}.xlsx`);

  return {
    receptions: recs.length,
    ventes: vts.length,
    mouvements: mvs.length,
    reglements: rgs.length,
    operations: opsP.length,
    calamites: calamites.lignes.length,
    onglets: wb.SheetNames.length,
  };
}
